import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, CheckCircle2, Circle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';
import { STATUS_OPTIONS, StatusBadge } from '../components/inbox/StatusBadge';
import { inputStyle, labelStyle } from '../components/quote/quoteStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STEPS = STATUS_OPTIONS.filter((s) => s.value !== 'cancelled');
const KIND_LABEL = { quote: 'Quick quote request', questionnaire: 'Quote request', order: 'Order' };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const Timeline = ({ status }) => {
  const idx = STEPS.findIndex((s) => s.value === status);
  return (
    <ol data-testid="status-timeline" style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0', display: 'grid', gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`, gap: '0.5rem' }}>
      {STEPS.map((s, i) => {
        const done = i <= idx;
        return (
          <li key={s.value} style={{ textAlign: 'center', color: done ? 'var(--brand-primary)' : 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: done ? '700' : '500' }}>
            {done ? <CheckCircle2 size={22} /> : <Circle size={22} />}<br />{s.label}
          </li>
        );
      })}
    </ol>
  );
};

const StatusPage = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ reference_id: params.get('id') || '', email: '' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/status-lookup`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check the reference number and email.');
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEOHelmet title="Check Request Status | Legal Document Specialist" description="Check the status of your quote request or order using your reference number and email address." canonical="/status" />
      <Header />
      <Breadcrumbs />
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '150px 0 100px 0', minHeight: '80vh' }}>
        <div className="container">
          <div style={{ maxWidth: '680px' }}>
            <p style={{ color: 'var(--brand-primary)', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.8rem', marginBottom: '1rem' }}>Request Status</p>
            <h1 className="heading-1" data-testid="status-page-title" style={{ marginBottom: '1rem', fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>Where is my request?</h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '2.5rem' }}>Enter the reference number from your confirmation email and the email address you used. No phone call needed.</p>

            <form onSubmit={submit} data-testid="status-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr auto', gap: '1rem', alignItems: 'end', backgroundColor: 'var(--bg-card)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border-medium)' }} className="status-form">
              <div>
                <label htmlFor="status-reference" style={labelStyle}>Reference #</label>
                <input id="status-reference" data-testid="status-reference-input" required value={form.reference_id} onChange={(e) => setForm({ ...form, reference_id: e.target.value })} placeholder="e.g. 10012" style={inputStyle} />
              </div>
              <div>
                <label htmlFor="status-email" style={labelStyle}>Email used</label>
                <input id="status-email" data-testid="status-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" style={inputStyle} />
              </div>
              <button type="submit" className="btn-primary" data-testid="status-submit-button" disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem 1.4rem' }}>
                <Search size={18} /> {loading ? 'Checking...' : 'Check'}
              </button>
            </form>

            {error && <p data-testid="status-error" role="alert" style={{ marginTop: '1.25rem', padding: '1rem', borderRadius: '8px', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030' }}>{error}</p>}

            {result && (
              <div data-testid="status-result" style={{ marginTop: '2rem', backgroundColor: 'var(--bg-card)', padding: '2rem', borderRadius: '12px', border: '2px solid var(--brand-primary)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <p style={{ margin: '0 0 0.25rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{KIND_LABEL[result.kind]} · {result.service_label}</p>
                    <h2 className="heading-2" style={{ margin: 0, fontSize: '1.6rem' }}>#{result.id}</h2>
                  </div>
                  <StatusBadge status={result.status} />
                </div>
                {result.status !== 'cancelled' && <Timeline status={result.status} />}
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 0.5rem' }}>Received {fmt(result.created_at)}{result.updated_at ? ` · Last updated ${fmt(result.updated_at)}` : ''}</p>
                {result.history.length > 0 && (
                  <ul data-testid="status-history" style={{ margin: '1rem 0 0', paddingLeft: '1.1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    {result.history.slice().reverse().map((h, i) => <li key={i}><strong style={{ color: 'var(--text-primary)' }}>{h.label}</strong> · {fmt(h.at)}{h.note ? ` — ${h.note}` : ''}</li>)}
                  </ul>
                )}
                <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Questions? Reply to your confirmation email or write to <a href="mailto:support@legaldocspecialist.com" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>support@legaldocspecialist.com</a>.</p>
              </div>
            )}

            <p style={{ marginTop: '2.5rem', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
              Don't have a request yet? <Link to="/quote" data-testid="status-to-quote-link" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>Start a quote questionnaire</Link>.
            </p>
          </div>
        </div>
      </section>
      <Footer />
      <style>{`@media (max-width: 700px) { .status-form { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
};

export default StatusPage;
